import React, { useRef, useEffect, useState } from "react";
import mapboxgl from "!mapbox-gl"; // eslint-disable-line import/no-webpack-loader-syntax
import "mapbox-gl/dist/mapbox-gl.css";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";

const DEFAULT_CENTER = [-70.703692, 19.415888];

const hasValidLocation = (candidate) => {
  if (!candidate) return false;

  const latitude = Number(candidate.latitude);
  const longitude = Number(candidate.longitude);

  return !Number.isNaN(latitude) && !Number.isNaN(longitude);
};

const LocationPicker = ({
  onLocationSelect,
  location,
  requestLocationSignal = 0,
}) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const initialLocationRef = useRef(location);
  const onLocationSelectRef = useRef(onLocationSelect);
  const [locationMessage, setLocationMessage] = useState("");
  const [permissionBlocked, setPermissionBlocked] = useState(false);

  useEffect(() => {
    onLocationSelectRef.current = onLocationSelect;
  }, [onLocationSelect]);

  const requestUserLocation = () => {
    if (!navigator.geolocation) {
      setPermissionBlocked(false);
      setLocationMessage("Tu navegador no soporta geolocalizacion.");
      return;
    }

    if (!window.isSecureContext) {
      setPermissionBlocked(false);
      setLocationMessage(
        "La geolocalizacion requiere un contexto seguro o localhost."
      );
      return;
    }

    setPermissionBlocked(false);
    setLocationMessage("Solicitando tu ubicacion...");

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const userLocation = [coords.longitude, coords.latitude];

        if (!mapRef.current) return;

        mapRef.current.flyTo({ center: userLocation, zoom: 14 });

        if (markerRef.current) {
          markerRef.current.setLngLat(userLocation);
        } else {
          markerRef.current = new mapboxgl.Marker()
            .setLngLat(userLocation)
            .addTo(mapRef.current);
        }

        onLocationSelectRef.current?.({
          longitude: coords.longitude,
          latitude: coords.latitude,
        });
        setPermissionBlocked(false);
        setLocationMessage("Ubicacion detectada automaticamente.");
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          setPermissionBlocked(true);
          setLocationMessage(
            "El permiso de ubicacion fue bloqueado. Revisa el permiso del navegador e intenta otra vez."
          );
          return;
        }

        if (error.code === error.POSITION_UNAVAILABLE) {
          setPermissionBlocked(false);
          setLocationMessage("No se pudo determinar tu ubicacion actual.");
          return;
        }

        if (error.code === error.TIMEOUT) {
          setPermissionBlocked(false);
          setLocationMessage(
            "La solicitud de ubicacion expiro. Intenta nuevamente."
          );
          return;
        }

        setPermissionBlocked(false);
        setLocationMessage("No se pudo obtener tu ubicacion.");
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000,
      }
    );
  };

  useEffect(() => {
    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;
    const initialLocation = initialLocationRef.current;
    const initialCenter = hasValidLocation(initialLocation)
      ? [Number(initialLocation.longitude), Number(initialLocation.latitude)]
      : DEFAULT_CENTER;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/streets-v11",
      center: initialCenter,
      zoom: 8,
    });

    mapRef.current = map;

    //Single source of truth for marker
    const setMarkerAndNotify = (lng, lat) => {
      if (markerRef.current) {
        markerRef.current.setLngLat([lng, lat]);
      } else {
        markerRef.current = new mapboxgl.Marker()
          .setLngLat([lng, lat])
          .addTo(map);
      }

      onLocationSelectRef.current?.({ longitude: lng, latitude: lat });
    };

    const coordinatesGeocoder = (query) => {
      const matches = query.match(/^[ ]*(-?\d+\.?\d*)[, ]+(-?\d+\.?\d*)[ ]*$/);
      if (!matches) return null;

      const a = Number(matches[1]);
      const b = Number(matches[2]);
      const geocodes = [];

      const coordinateFeature = (lng, lat, label) => ({
        type: "Feature",
        type_name: "coordinate",
        geometry: {
          type: "Point",
          coordinates: [lng, lat],
        },
        center: [lng, lat],
        place_name: label,
        place_type: ["coordinate"],
      });

      // Case 1: clearly lng, lat
      if (a < -90 || a > 90) {
        geocodes.push(coordinateFeature(a, b, `Lat: ${b}, Lng: ${a}`));
        return geocodes;
      }

      // Case 2: clearly lat, lng
      if (b < -90 || b > 90) {
        geocodes.push(coordinateFeature(b, a, `Lat: ${a}, Lng: ${b}`));
        return geocodes;
      }

      // Case 3: ambiguous → assume Google Maps (lat, lng)
      geocodes.push(coordinateFeature(b, a, `Lat: ${a}, Lng: ${b}`));

      return geocodes;
    };

    // Geocoder
    const geocoder = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken,
      localGeocoder: coordinatesGeocoder,
      placeholder: "Enter coordinates (lat,lng)",
      mapboxgl,
      reverseGeocode: true,
      zoom: 14,
      marker: false, // prevent duplicate markers
    });

    mapRef.current.addControl(geocoder);
    mapRef.current.addControl(new mapboxgl.NavigationControl());

    //Geocoder selection
    geocoder.on("result", (e) => {
      const [lng, lat] = e.result.center;
      setMarkerAndNotify(lng, lat);
      map.flyTo({ center: [lng, lat], zoom: 14 });
    });

    //Map click
    map.on("click", (e) => {
      const { lng, lat } = e.lngLat;
      setMarkerAndNotify(lng, lat);
      setPermissionBlocked(false);
      setLocationMessage("");
    });

    if (!hasValidLocation(initialLocation)) {
      requestUserLocation();
    }

    return () => map.remove();
  }, []);

  //React to external location updates
  useEffect(() => {
    if (!mapRef.current || !location) return;

    if (!hasValidLocation(location)) {
      mapRef.current.flyTo({ center: DEFAULT_CENTER, zoom: 8 });
      return;
    }

    const { longitude, latitude } = location;

    if (markerRef.current) {
      markerRef.current.setLngLat([longitude, latitude]);
    } else {
      markerRef.current = new mapboxgl.Marker()
        .setLngLat([longitude, latitude])
        .addTo(mapRef.current);
    }

    mapRef.current.flyTo({
      center: [longitude, latitude],
      zoom: 16,
    });
  }, [location]);

  useEffect(() => {
    if (!requestLocationSignal || !mapRef.current) return;

    requestUserLocation();
  }, [requestLocationSignal]);

  return (
    <div style={{ position: "relative" }}>
      <div
        ref={mapContainerRef}
        className="map-container"
        style={{
          height: "clamp(320px, 48vh, 460px)",
          width: "100%",
          borderRadius: 24,
          overflow: "hidden",
          border: "1px solid rgba(148, 163, 184, 0.24)",
          boxShadow: "0 18px 34px rgba(15, 23, 42, 0.08)",
        }}
      />
      <div
        style={{
          position: "absolute",
          left: 16,
          bottom: 16,
          padding: "10px 14px",
          borderRadius: 14,
          background: "rgba(255,255,255,0.9)",
          border: "1px solid rgba(148, 163, 184, 0.2)",
          color: "#334155",
          fontSize: 12,
          fontWeight: 600,
          backdropFilter: "blur(8px)",
          pointerEvents: "none",
        }}
      >
        Haz clic en el mapa o busca coordenadas para fijar la ubicacion.
      </div>
      {permissionBlocked && (
        <div
          style={{
            position: "absolute",
            right: 16,
            top: 60,
            padding: "8px 12px",
            borderRadius: 12,
            background: "rgba(220, 38, 38, 0.12)",
            border: "1px solid rgba(220, 38, 38, 0.24)",
            color: "#b91c1c",
            fontSize: 12,
            fontWeight: 700,
            boxShadow: "0 12px 22px rgba(15, 23, 42, 0.08)",
          }}
        >
          Permiso bloqueado en el navegador
        </div>
      )}
      {locationMessage && (
        <div
          style={{
            position: "absolute",
            left: 16,
            top: 16,
            maxWidth: "calc(100% - 180px)",
            padding: "10px 14px",
            borderRadius: 14,
            background: "rgba(255,255,255,0.94)",
            border: "1px solid rgba(148, 163, 184, 0.2)",
            color: "#334155",
            fontSize: 12,
            fontWeight: 600,
            boxShadow: "0 12px 22px rgba(15, 23, 42, 0.08)",
          }}
        >
          {locationMessage}
        </div>
      )}
    </div>
  );
};

export default LocationPicker;
